import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { hubAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
    MessageSquare, Paperclip, Send, Trash2, Mic,
    FileText, Download, Image as ImageIcon, Film, Music, File as FileIcon,
    MessageCircle, Pin, PinOff, AtSign, Eye, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { InfoHint } from '@/components/common/Hint';
import { Linkify } from '@/lib/linkify';
import { VoiceMessagePlayer } from './VoiceMessagePlayer';
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog';
import { MentionTextInput, extractMentionIds, mentionIdsToNames } from './MentionTextInput';
import { QuickReactionBar } from './QuickReactionBar';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { VoiceRecorderPanel } from '@/components/common/VoiceRecorderPanel';
import { sendProjectVoiceDraft } from './sendProjectVoiceDraft';
import { ProjectAttachments } from './ProjectAttachments';
import { publishProjectFiles } from './projectFilePublication';
import { validateProjectFiles } from './projectFileValidation';
import { getPublication, subscribePublication, updatePublication } from '../prospect/attachments/publicationState';

const VOICE_MAX_SECONDS = 180; // 3 minutes (choix produit — voir handoff)

// ============ Helpers ============
const fmtSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const now = new Date();
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

// Fetch une pièce jointe avec authent et renvoie une URL Blob (utilisable dans <img/video/audio/a>).
// Cache module-level pour éviter les re-downloads (clef = URL).
const _blobUrlCache = new Map();
const _blobUrlPending = new Map();

export async function fetchAuthBlobUrl(url) {
    if (_blobUrlCache.has(url)) return _blobUrlCache.get(url);
    if (_blobUrlPending.has(url)) return _blobUrlPending.get(url);
    const p = (async () => {
        const token = localStorage.getItem('valnexo_token');
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        _blobUrlCache.set(url, objectUrl);
        _blobUrlPending.delete(url);
        return objectUrl;
    })();
    _blobUrlPending.set(url, p);
    return p;
}

// Pré-charge sans bloquer — utile pour les vocaux/PDFs (préparation en arrière-plan)
export function prefetchAuthBlobUrl(url) {
    if (!url) return;
    fetchAuthBlobUrl(url).catch(() => { /* silencieux */ });
}

// Composant d'attachment avec preview inline (image/audio/video/pdf) + aperçu (eye).
function AttachmentTile({ projectId, messageId, attachment }) {
    const kind = attachment.kind || 'file';
    const inlineUrl = hubAPI.projectWallAttachmentUrl(projectId, messageId, attachment.id, 'inline');
    const downloadUrl = hubAPI.projectWallAttachmentUrl(projectId, messageId, attachment.id, 'attachment');
    // Rendu instantané si le blob est déjà en cache (pas de flash "Chargement…")
    const [blobUrl, setBlobUrl] = useState(() => _blobUrlCache.get(inlineUrl) || null);
    const [error, setError] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Charger blob pour les médias qu'on affiche inline (image/audio/video)
    useEffect(() => {
        if (!['image', 'audio', 'video'].includes(kind)) return;
        if (_blobUrlCache.has(inlineUrl)) {
            setBlobUrl(_blobUrlCache.get(inlineUrl));
            return;
        }
        let alive = true;
        fetchAuthBlobUrl(inlineUrl).then((u) => { if (alive) setBlobUrl(u); }).catch((e) => { if (alive) setError(e.message); });
        return () => { alive = false; };
    }, [inlineUrl, kind]);

    // Pré-charger les PDFs en arrière-plan (idle) — pour un aperçu instantané au clic
    useEffect(() => {
        if (kind !== 'pdf') return;
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
        const id = idle(() => prefetchAuthBlobUrl(inlineUrl));
        return () => {
            if (window.cancelIdleCallback && typeof id === 'number') window.cancelIdleCallback(id);
        };
    }, [inlineUrl, kind]);

    // Détermine si le type est prévisualisable (aperçu inline)
    const previewable = ['image', 'pdf', 'video', 'audio'].includes(kind)
        || (attachment.mime_type && (attachment.mime_type.startsWith('text/') || attachment.mime_type === 'application/json'));

    const downloadFile = useCallback(async () => {
        try {
            const url = await fetchAuthBlobUrl(downloadUrl);
            const a = document.createElement('a');
            a.href = url;
            a.download = attachment.original_filename || 'fichier';
            document.body.appendChild(a); a.click(); a.remove();
        } catch (e) {
            toast.error('Téléchargement impossible');
        }
    }, [downloadUrl, attachment]);

    if (kind === 'image') {
        return (
            <>
                <div className="rounded-lg overflow-hidden border border-slate-200 max-w-sm group relative" data-testid={`pw-att-image-${attachment.id}`}>
                    {error && <div className="p-3 text-xs text-red-500">Image indisponible</div>}
                    {!error && blobUrl && (
                        <button
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            className="w-full block"
                            title="Agrandir"
                            data-testid={`pw-att-preview-${attachment.id}`}
                        >
                            <img src={blobUrl} alt={attachment.original_filename} className="w-full h-auto max-h-80 object-contain bg-slate-50 hover:opacity-90 transition-opacity cursor-zoom-in" />
                        </button>
                    )}
                    {!error && !blobUrl && <div className="p-6 text-xs text-slate-400 bg-slate-50">Chargement de l&apos;image…</div>}
                    <div className="px-2 py-1 text-[10px] text-slate-500 flex items-center justify-between bg-white">
                        <span className="truncate">{attachment.original_filename}</span>
                        <div className="flex gap-1.5 items-center">
                            <button onClick={() => setPreviewOpen(true)} className="text-slate-500 hover:text-indigo-600" title="Aperçu">
                                <Eye className="w-3 h-3" />
                            </button>
                            <button onClick={downloadFile} className="text-indigo-600 hover:text-indigo-800" title="Télécharger" data-testid={`pw-att-dl-${attachment.id}`}>
                                <Download className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>
                {previewOpen && (
                    <AttachmentPreviewDialog
                        open={previewOpen}
                        onOpenChange={setPreviewOpen}
                        inlineUrl={inlineUrl}
                        downloadUrl={downloadUrl}
                        filename={attachment.original_filename}
                        kind={kind}
                        mime={attachment.mime_type}
                        size={fmtSize(attachment.file_size)}
                    />
                )}
            </>
        );
    }
    if (kind === 'audio') {
        return (
            <div data-testid={`pw-att-audio-${attachment.id}`}>
                <VoiceMessagePlayer
                    blobUrl={blobUrl}
                    seed={attachment.id}
                    testIdPrefix={`pw-voice-${attachment.id}`}
                    filename={attachment.original_filename}
                    loading={!blobUrl && !error}
                    onDownload={downloadFile}
                    downloadUrl={downloadUrl}
                />
            </div>
        );
    }
    if (kind === 'video') {
        return (
            <div className="rounded-lg overflow-hidden border border-slate-200 bg-black max-w-md" data-testid={`pw-att-video-${attachment.id}`}>
                {blobUrl ? (
                    <video controls src={blobUrl} className="w-full max-h-96" preload="metadata" />
                ) : (
                    <div className="p-6 text-xs text-white/70">Chargement de la vidéo…</div>
                )}
                <div className="px-2 py-1 text-[10px] text-slate-100 flex items-center justify-between bg-slate-900">
                    <span className="truncate">{attachment.original_filename} · {fmtSize(attachment.file_size)}</span>
                    <button onClick={downloadFile} className="text-white/80 hover:text-white" title="Télécharger">
                        <Download className="w-3 h-3" />
                    </button>
                </div>
            </div>
        );
    }
    if (kind === 'pdf') {
        return (
            <>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 max-w-sm flex items-center gap-3 w-full" data-testid={`pw-att-pdf-${attachment.id}`}>
                    <FileText className="w-8 h-8 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{attachment.original_filename}</div>
                        <div className="text-[10px] text-slate-500">PDF · {fmtSize(attachment.file_size)}</div>
                    </div>
                    <button
                        onClick={() => setPreviewOpen(true)}
                        className="w-8 h-8 rounded-full bg-white hover:bg-red-100 border border-red-200 text-red-600 flex items-center justify-center shrink-0"
                        title="Aperçu"
                        data-testid={`pw-att-preview-${attachment.id}`}
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                    <button
                        onClick={downloadFile}
                        className="w-8 h-8 rounded-full bg-white hover:bg-red-100 border border-red-200 text-red-600 flex items-center justify-center shrink-0"
                        title="Télécharger"
                    >
                        <Download className="w-4 h-4" />
                    </button>
                </div>
                {previewOpen && (
                    <AttachmentPreviewDialog
                        open={previewOpen}
                        onOpenChange={setPreviewOpen}
                        inlineUrl={inlineUrl}
                        downloadUrl={downloadUrl}
                        filename={attachment.original_filename}
                        kind={kind}
                        mime={attachment.mime_type}
                        size={fmtSize(attachment.file_size)}
                    />
                )}
            </>
        );
    }
    // Fichier générique
    return (
        <>
            <div className="rounded-lg border border-slate-200 bg-white p-3 max-w-sm flex items-center gap-3 w-full" data-testid={`pw-att-file-${attachment.id}`}>
                <FileIcon className="w-6 h-6 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{attachment.original_filename}</div>
                    <div className="text-[10px] text-slate-500">{fmtSize(attachment.file_size)}</div>
                </div>
                {previewable && (
                    <button
                        onClick={() => setPreviewOpen(true)}
                        className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"
                        title="Aperçu"
                        data-testid={`pw-att-preview-${attachment.id}`}
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                )}
                <button
                    onClick={downloadFile}
                    className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"
                    title="Télécharger"
                >
                    <Download className="w-4 h-4" />
                </button>
            </div>
            {previewOpen && (
                <AttachmentPreviewDialog
                    open={previewOpen}
                    onOpenChange={setPreviewOpen}
                    inlineUrl={inlineUrl}
                    downloadUrl={downloadUrl}
                    filename={attachment.original_filename}
                    kind={kind}
                    mime={attachment.mime_type}
                    size={fmtSize(attachment.file_size)}
                />
            )}
        </>
    );
}

// Rendu du contenu avec mentions surlignées (@Prénom Nom)
function RenderMessageContent({ content, mentionIds, projectMembers }) {
    if (!content) return null;
    const mentionNames = mentionIdsToNames(mentionIds, projectMembers);
    return (
        <p className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5 break-words">
            <Linkify text={content} mentionNames={mentionNames} />
        </p>
    );
}

// Message unitaire
function MessageItem({ msg, projectId, currentUserId, canDelete, canPin, projectMembers, onDelete, onReaction, onAddComment, onDeleteComment, onTogglePin, onCommentReaction, onEdit }) {
    const [showComments, setShowComments] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [postingComment, setPostingComment] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(msg.content || '');
    const [savingEdit, setSavingEdit] = useState(false);

    const author = msg.author || {};
    const initials = `${(author.first_name?.[0] || '?')}${(author.last_name?.[0] || '')}`;
    const isMine = msg.author_id === currentUserId;
    const isPinned = !!msg.is_pinned;

    // Édition autorisée : auteur + AUCUNE réaction active + AUCUN commentaire
    const hasReactions = Object.values(msg.reactions || {}).some((users) => (users || []).length > 0);
    const hasComments = (msg.comments || []).length > 0;
    const canEdit = isMine && !hasReactions && !hasComments;

    const submitComment = async () => {
        if (!commentText.trim()) return;
        setPostingComment(true);
        try {
            const mentions = extractMentionIds(commentText.trim(), projectMembers);
            await onAddComment(msg.id, commentText.trim(), mentions);
            setCommentText('');
            if (mentions.length > 0) {
                toast.success(`${mentions.length} personne${mentions.length > 1 ? 's' : ''} mentionnée${mentions.length > 1 ? 's' : ''}`);
            }
        } finally {
            setPostingComment(false);
        }
    };

    const submitEdit = async () => {
        if (!editText.trim()) { toast.error('Le message ne peut pas être vide'); return; }
        setSavingEdit(true);
        try {
            await onEdit(msg.id, editText.trim());
            setIsEditing(false);
        } finally {
            setSavingEdit(false);
        }
    };

    return (
        <div
            className={cn(
                "flex gap-3 rounded-md transition-colors",
                isPinned && "bg-amber-50/50 border border-amber-200 p-2 -mx-2",
                msg._pending && "opacity-70",
            )}
            data-testid={`pw-msg-${msg.id}`}
        >
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-xs flex items-center justify-center font-semibold shrink-0 mt-1">
                {initials}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">
                        {author.first_name || 'Utilisateur'} {author.last_name || ''}
                    </span>
                    <span className="text-[10px] text-slate-400">{fmtDate(msg.created_at)}</span>
                    {msg.edited_at && (
                        <span className="text-[9px] text-slate-400 italic" title={`Modifié le ${fmtDate(msg.edited_at)}`}>(modifié)</span>
                    )}
                    {msg._pending && (
                        <span className="text-[9px] text-indigo-500 italic animate-pulse" data-testid={`pw-msg-sending-${msg.id}`}>envoi…</span>
                    )}
                    {isMine && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Moi</Badge>}
                    {isPinned && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5" data-testid={`pw-msg-pinned-${msg.id}`}>
                            <Pin className="w-2.5 h-2.5" /> Épinglé
                        </span>
                    )}
                </div>
                {isEditing ? (
                    <div className="mt-1 space-y-1" data-testid={`pw-msg-edit-${msg.id}`}>
                        <Textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') { setIsEditing(false); setEditText(msg.content || ''); }
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submitEdit(); }
                            }}
                            rows={2}
                            disabled={savingEdit}
                            autoFocus
                            className="text-sm resize-none"
                            data-testid={`pw-msg-edit-input-${msg.id}`}
                        />
                        <div className="flex items-center gap-2 text-[10px]">
                            <Button size="sm" onClick={submitEdit} disabled={savingEdit || !editText.trim()} data-testid={`pw-msg-edit-save-${msg.id}`}>
                                {savingEdit ? '…' : 'Enregistrer'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditText(msg.content || ''); }} disabled={savingEdit}>
                                Annuler
                            </Button>
                            <span className="text-slate-400 ml-auto">Ctrl+Entrée pour valider · Échap pour annuler</span>
                        </div>
                    </div>
                ) : (
                    <RenderMessageContent content={msg.content} mentionIds={msg.mentions} projectMembers={projectMembers} />
                )}
                {(msg.attachments || []).length > 0 && (
                    <ProjectAttachments projectId={projectId} messageId={msg.id} attachments={msg.attachments} />
                )}
                <QuickReactionBar
                    reactions={msg.reactions}
                    currentUserId={currentUserId}
                    onToggle={(emoji) => onReaction(msg.id, emoji)}
                    testIdPrefix={`pw-msg-${msg.id}-react`}
                    members={projectMembers}
                />

                {/* Actions bar */}
                <div className="flex items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-slate-500 flex-wrap">
                    <button
                        onClick={() => setShowComments((v) => !v)}
                        className="hover:text-indigo-600 flex items-center gap-1"
                        data-testid={`pw-msg-toggle-comments-${msg.id}`}
                    >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {(msg.comments || []).length > 0 ? `${msg.comments.length} commentaire${msg.comments.length > 1 ? 's' : ''}` : 'Commenter'}
                    </button>
                    {canEdit && !isEditing && (
                        <button
                            onClick={() => { setIsEditing(true); setEditText(msg.content || ''); }}
                            className="hover:text-indigo-600 flex items-center gap-1"
                            data-testid={`pw-msg-edit-btn-${msg.id}`}
                            title="Modifier ce message (possible tant que personne n'a réagi ou commenté)"
                        >
                            <span className="text-[13px]">✎</span> Modifier
                        </button>
                    )}
                    {canPin && (
                        <button
                            onClick={() => onTogglePin(msg.id)}
                            className={cn("flex items-center gap-1 hover:text-amber-600", isPinned && "text-amber-600 font-medium")}
                            data-testid={`pw-msg-toggle-pin-${msg.id}`}
                            title={isPinned ? "Désépingler" : "Épingler en haut du mur"}
                        >
                            {isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                            {isPinned ? "Désépingler" : "Épingler"}
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => { if (window.confirm('Supprimer ce message ?')) onDelete(msg.id); }}
                            className="hover:text-red-600 flex items-center gap-1"
                            data-testid={`pw-msg-delete-${msg.id}`}
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Supprimer
                        </button>
                    )}
                </div>

                {/* Comments thread */}
                {showComments && (
                    <div className="mt-2 border-l-2 border-slate-200 pl-3 space-y-2" data-testid={`pw-comments-${msg.id}`}>
                        {(msg.comments || []).map((c) => {
                            const cAuthor = c.author || {};
                            const cInit = `${(cAuthor.first_name?.[0] || '?')}${(cAuthor.last_name?.[0] || '')}`;
                            const canDelC = c.author_id === currentUserId || canDelete;
                            return (
                                <div key={c.id} className="flex items-start gap-2 group/comment" data-testid={`pw-comment-${c.id}`}>
                                    <div className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[9px] flex items-center justify-center font-semibold shrink-0 mt-0.5">
                                        {cInit}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-xs font-semibold text-slate-700">
                                                {cAuthor.first_name || 'User'} {cAuthor.last_name || ''}
                                            </span>
                                            <span className="text-[9px] text-slate-400">{fmtDate(c.created_at)}</span>
                                        </div>
                                        <div className="text-xs text-slate-600 whitespace-pre-wrap break-words">
                                            <RenderMessageContent content={c.content} mentionIds={c.mentions} projectMembers={projectMembers} />
                                        </div>
                                        <QuickReactionBar
                                            compact
                                            reactions={c.reactions}
                                            currentUserId={currentUserId}
                                            onToggle={(emoji) => onCommentReaction(msg.id, c.id, emoji)}
                                            testIdPrefix={`pw-comment-${c.id}-react`}
                                            members={projectMembers}
                                        />
                                    </div>
                                    {canDelC && (
                                        <button
                                            onClick={() => { if (window.confirm('Supprimer le commentaire ?')) onDeleteComment(msg.id, c.id); }}
                                            className="opacity-0 group-hover/comment:opacity-100 text-slate-400 hover:text-red-500 transition-opacity"
                                            data-testid={`pw-comment-delete-${c.id}`}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        <div className="flex gap-2">
                            <MentionTextInput
                                value={commentText}
                                onChange={setCommentText}
                                onSubmit={submitComment}
                                members={projectMembers}
                                placeholder="Écrire un commentaire… (@ pour mentionner)"
                                disabled={postingComment}
                                testId={`pw-comment-input-${msg.id}`}
                            />
                            <Button size="sm" variant="ghost" onClick={submitComment} disabled={!commentText.trim() || postingComment} data-testid={`pw-comment-send-${msg.id}`}>
                                <Send className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ============ Composant principal ============
export function ProjectWall({ projectId, canWrite, project, allMembers }) {
    const { user } = useAuth();
    const currentUserId = user?.id;
    const isAdmin = user?.is_admin || user?.role === 'admin_full' || user?.role === 'admin';
    const isOwner = project?.owner_id === currentUserId;
    const canPin = isAdmin || isOwner;
    // Membres du projet (owner + members), pour la mention @
    const projectMembers = useMemo(() => {
        const ids = new Set([...(project?.members || []), project?.owner_id]);
        return (allMembers || []).filter(m => ids.has(m.id));
    }, [allMembers, project?.members, project?.owner_id]);

    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const publicationScope = `project-files:${currentUserId}:${projectId}`;
    const initialDraft = useRef(getPublication(publicationScope)).current;
    const [text, setText] = useState(initialDraft?.text || '');
    const [sending, setSending] = useState(initialDraft?.status === 'sending');
    const [selectedFiles, setSelectedFiles] = useState(initialDraft?.files || []);
    const [fileError, setFileError] = useState(initialDraft?.error || null);
    const pendingFiles = useRef(initialDraft || null);
    const [uploadProgress, setUploadProgress] = useState({}); // { fileName: pct }
    const [mentionQuery, setMentionQuery] = useState(null); // null = fermé, sinon string tapée après @
    const [mentionIndex, setMentionIndex] = useState(0);
    const composerRef = useRef(null);
    const fileInputRef = useRef(null);
    const scrollRef = useRef(null);

    const voice = useVoiceRecorder(canWrite ? projectId : null, VOICE_MAX_SECONDS);
    const voiceActive = voice.phase !== 'idle';
    const captureBusy = ['requesting', 'recording', 'stopping'].includes(voice.phase);
    const voiceSendingRef = useRef(false);
    const pendingVoice = useRef({ messageId: null });
    const [voiceError, setVoiceError] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await hubAPI.listProjectWall(projectId);
            const msgs = r.data || [];
            setMessages(msgs);
        } catch (e) {
            // 403 = non-membre : on affiche simplement le message read-only ci-dessous, pas de toast.
            if (e.response?.status !== 403) {
                toast.error(e.response?.data?.detail || 'Impossible de charger le mur');
            }
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => subscribePublication(publicationScope, ({ entry, note }) => {
        pendingFiles.current = entry;
        setSending(entry?.status === 'sending'); setFileError(entry?.error || null);
        setSelectedFiles(entry?.files || []);
        if (entry) {
            setText(entry.text);
            setUploadProgress(Object.fromEntries(entry.files.map(item => [item.file.name, entry.progress?.[item.id] || 0])));
        } else { setText(''); setUploadProgress({}); setMentionQuery(null); }
        if (note) setMessages(previous => previous.some(item => item.id === note.id) ? previous.map(item => item.id === note.id ? note : item) : [...previous, note]);
    }), [publicationScope]);

    // Scroll bottom on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages.length]);

    // (helper `extractMentionIds` importé depuis `MentionTextInput` — même logique
    // partagée avec le champ commentaire.)
    const extractMentions = useCallback((raw) => extractMentionIds(raw, projectMembers), [projectMembers]);

    const send = async () => {
        if (sending || voiceActive || getPublication(publicationScope)?.status === 'sending') return;
        const content = text.trim();
        if (!content && !selectedFiles.length && !pendingFiles.current?.messageId) return;
        await publishProjectFiles(publicationScope, { projectId, text, files: selectedFiles, mentions: extractMentions(content),
            requestId: pendingFiles.current?.requestId || crypto.randomUUID(), messageId: pendingFiles.current?.messageId || null });
    };

    const handleFilePick = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length || sending || voiceActive) return;
        const attachedCount = messages.find(message => message.id === pendingFiles.current?.messageId)?.attachments?.length || 0;
        const validationError = validateProjectFiles(files, selectedFiles.length + attachedCount);
        if (validationError) { setFileError(validationError); return; }
        const next = [...selectedFiles, ...files.map(file => ({ id: crypto.randomUUID(), file }))];
        if (pendingFiles.current) updatePublication(publicationScope, { ...pendingFiles.current, files: next });
        else setSelectedFiles(next);
        setFileError(null);
    };

    // Aucun appel serveur dans Arrêter/Annuler : seul ce clic explicite publie.
    const sendVoice = async () => {
        if (!canWrite || voice.phase !== 'ready' || !voice.draft || sending || voiceSendingRef.current) return;
        voiceSendingRef.current = true;
        setSending(true); setVoiceError(null);
        const file = voice.draft.file;
        try {
            const message = await sendProjectVoiceDraft({
                projectId, draft: voice.draft, content: text.trim(), mentions: extractMentions(text.trim()), pending: pendingVoice.current,
                onProgress: event => { if (event.total) setUploadProgress({ [file.name]: Math.round(event.loaded / event.total * 100) }); },
            });
            setMessages(previous => previous.some(m => m.id === message.id) ? previous.map(m => m.id === message.id ? message : m) : [...previous, message]);
            pendingVoice.current = { messageId: null };
            setText(''); setMentionQuery(null); voice.discard();
        } catch {
            setVoiceError('Envoi du vocal non confirmé. Votre enregistrement est conservé ; vous pouvez réessayer.');
        } finally {
            setUploadProgress({}); setSending(false); voiceSendingRef.current = false;
        }
    };

    const discardVoice = async () => {
        if (sending || voiceSendingRef.current) return;
        voiceSendingRef.current = true;
        try {
            // Seulement après un envoi déjà confirmé par clic puis échoué.
            if (pendingVoice.current.messageId) {
                try { await hubAPI.deleteProjectWallMessage(projectId, pendingVoice.current.messageId); }
                catch (error) { if (error.response?.status !== 404) throw error; }
            }
            pendingVoice.current = { messageId: null };
            voice.discard(); setVoiceError(null);
        } catch {
            setVoiceError('Suppression impossible pour le moment. Votre vocal est conservé.');
        } finally { voiceSendingRef.current = false; }
    };

    // ---- Actions message ----
    const onDelete = useCallback(async (messageId) => {
        try {
            await hubAPI.deleteProjectWallMessage(projectId, messageId);
            setMessages((prev) => prev.filter((m) => m.id !== messageId));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suppression impossible');
        }
    }, [projectId]);

    const onReaction = useCallback(async (messageId, emoji) => {
        try {
            const r = await hubAPI.toggleProjectWallReaction(projectId, messageId, emoji);
            setMessages((prev) => prev.map((m) => m.id === messageId ? r.data : m));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Réaction impossible');
        }
    }, [projectId]);

    const onAddComment = useCallback(async (messageId, content, mentions = []) => {
        try {
            const r = await hubAPI.commentProjectWallMessage(projectId, messageId, content, mentions);
            setMessages((prev) => prev.map((m) => m.id === messageId ? r.data : m));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Commentaire impossible');
        }
    }, [projectId]);

    const onDeleteComment = useCallback(async (messageId, commentId) => {
        try {
            const r = await hubAPI.deleteProjectWallComment(projectId, messageId, commentId);
            setMessages((prev) => prev.map((m) => m.id === messageId ? r.data : m));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suppression impossible');
        }
    }, [projectId]);

    const onTogglePin = useCallback(async (messageId) => {
        try {
            const r = await hubAPI.toggleProjectWallPin(projectId, messageId);
            const updated = r.data;
            toast.success(updated.is_pinned ? 'Message épinglé en haut du mur' : 'Message désépinglé');
            // Re-fetch pour respecter le tri (pinned first) et éviter les artefacts
            const list = await hubAPI.listProjectWall(projectId);
            setMessages(list.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Épinglage impossible');
        }
    }, [projectId]);

    const onCommentReaction = useCallback(async (messageId, commentId, emoji) => {
        try {
            const r = await hubAPI.toggleProjectWallCommentReaction(projectId, messageId, commentId, emoji);
            setMessages((prev) => prev.map((m) => m.id === messageId ? r.data : m));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Réaction impossible');
        }
    }, [projectId]);

    const onEditMessage = useCallback(async (messageId, content) => {
        // Auto-parse mentions à partir du nouveau texte
        const mentions = extractMentions(content);
        try {
            const r = await hubAPI.editProjectWallMessage(projectId, messageId, content, mentions);
            setMessages((prev) => prev.map((m) => m.id === messageId ? r.data : m));
            toast.success('Message modifié');
        } catch (e) {
            const status = e.response?.status;
            if (status === 409) {
                toast.error('Trop tard : quelqu\'un a déjà réagi ou commenté ce message');
            } else {
                toast.error(e.response?.data?.detail || 'Modification impossible');
            }
            throw e;
        }
    }, [projectId, extractMentions]);

    return (
        <Card data-testid="hp-project-wall">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-700 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Mur d&apos;échange
                    <InfoHint text="Discutez librement autour de ce projet : messages, photos, PDF, vidéos, mémos vocaux. Chaque membre du projet peut lire et poster ; les admins ont accès à tous les projets." />
                    <Badge variant="secondary" className="ml-auto text-[10px]">{messages.length}</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {useMemo(() => loading ? (
                    <div className="text-xs text-slate-400 py-6 text-center">Chargement…</div>
                ) : (
                    <div ref={scrollRef} className="space-y-4 max-h-[560px] overflow-y-auto overflow-x-hidden pr-2" style={{ contain: 'layout paint' }} data-testid="pw-messages-list">
                        {messages.length === 0 ? (
                            <div className="text-center py-8 text-slate-400">
                                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                <div className="text-xs">Aucun message. Lancez la discussion !</div>
                            </div>
                        ) : (
                            messages.map((m) => (
                                <MessageItem
                                    key={m.id}
                                    msg={m}
                                    projectId={projectId}
                                    currentUserId={currentUserId}
                                    canDelete={m.author_id === currentUserId || isAdmin}
                                    canPin={canPin}
                                    projectMembers={projectMembers}
                                    onDelete={onDelete}
                                    onReaction={onReaction}
                                    onAddComment={onAddComment}
                                    onDeleteComment={onDeleteComment}
                                    onTogglePin={onTogglePin}
                                    onCommentReaction={onCommentReaction}
                                    onEdit={onEditMessage}
                                />
                            ))
                        )}
                    </div>
                ), [loading, messages, projectId, currentUserId, isAdmin, canPin, projectMembers, onDelete, onReaction, onAddComment, onDeleteComment, onTogglePin, onCommentReaction, onEditMessage])}

                {/* Uploads en cours */}
                {Object.keys(uploadProgress).length > 0 && (
                    <div className="space-y-1 pt-2 border-t" data-testid="pw-upload-progress">
                        {Object.entries(uploadProgress).map(([name, pct]) => (
                            <div key={name}>
                                <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
                                    <span className="truncate">Envoi {name}</span><span>{pct}%</span>
                                </div>
                                <Progress value={pct} className="h-1" />
                            </div>
                        ))}
                    </div>
                )}

                {/* Composer */}
                {canWrite ? (
                    <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2" data-testid="pw-message-composer">
                        <VoiceRecorderPanel recorder={voice} prefix="pw" sending={sending} error={voiceError} onSend={sendVoice} onDiscard={discardVoice} />
                        {/* Autocomplete mentions */}
                        {mentionQuery !== null && (() => {
                            const q = mentionQuery.toLowerCase();
                            const matches = projectMembers.filter((m) => {
                                if (m.id === currentUserId) return false;
                                const full = `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase();
                                return !q || full.includes(q);
                            }).slice(0, 6);
                            if (matches.length === 0) return null;
                            const pick = (m) => {
                                const ta = composerRef.current;
                                if (!ta) return;
                                const val = text;
                                const caret = ta.selectionStart ?? val.length;
                                // Cherche le dernier '@' avant le caret
                                const before = val.slice(0, caret);
                                const atIdx = before.lastIndexOf('@');
                                if (atIdx < 0) return;
                                const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
                                const newVal = val.slice(0, atIdx) + `@${name} ` + val.slice(caret);
                                setText(newVal);
                                setMentionQuery(null);
                                setMentionIndex(0);
                                setTimeout(() => {
                                    ta.focus();
                                    const p = atIdx + name.length + 2;
                                    ta.setSelectionRange(p, p);
                                }, 0);
                            };
                            return (
                                <div className="border border-slate-200 rounded-md bg-white shadow-sm max-h-40 overflow-y-auto" data-testid="pw-mention-dropdown">
                                    <div className="text-[10px] text-slate-500 px-2 py-1 border-b bg-slate-50 flex items-center gap-1">
                                        <AtSign className="w-3 h-3" /> Mentionner un membre du projet
                                    </div>
                                    {matches.map((m, i) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => pick(m)}
                                            className={cn(
                                                "w-full text-left px-2 py-1.5 text-xs hover:bg-indigo-50 flex items-center gap-2",
                                                i === mentionIndex && "bg-indigo-50"
                                            )}
                                            data-testid={`pw-mention-opt-${m.id}`}
                                        >
                                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] flex items-center justify-center font-semibold shrink-0">
                                                {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                                            </span>
                                            <span>{m.first_name} {m.last_name}</span>
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}
                        <Textarea
                            ref={composerRef}
                            value={text}
                            onChange={(e) => {
                                const v = e.target.value;
                                setText(v);
                                if (pendingFiles.current && !pendingFiles.current.messageId) updatePublication(publicationScope, { ...pendingFiles.current, text: v });
                                // Détecte si on est en train de taper une mention (@…)
                                const caret = e.target.selectionStart ?? v.length;
                                const before = v.slice(0, caret);
                                const atIdx = before.lastIndexOf('@');
                                if (atIdx >= 0) {
                                    const between = before.slice(atIdx + 1);
                                    // Autoriser lettres/espaces (nom complet), pas de retour ligne
                                    if (/^[A-Za-zÀ-ÖØ-öø-ÿ\- ]{0,40}$/.test(between)) {
                                        // Vérifier qu'il n'y a pas plus d'un espace après (fin de mention)
                                        if (between.split(' ').length <= 3) {
                                            setMentionQuery(between);
                                            setMentionIndex(0);
                                            return;
                                        }
                                    }
                                }
                                setMentionQuery(null);
                            }}
                            onKeyDown={(e) => {
                                if (mentionQuery !== null) {
                                    if (e.key === 'Escape') { setMentionQuery(null); e.preventDefault(); return; }
                                }
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
                            }}
                            placeholder="Écrivez un message… tapez @ pour mentionner (Ctrl+Entrée = envoyer)"
                            rows={2}
                            disabled={sending || captureBusy || !!pendingVoice.current.messageId || !!pendingFiles.current?.messageId}
                            className="text-sm resize-none border-0 shadow-none focus-visible:ring-0"
                            data-testid="pw-composer-input"
                        />
                        {selectedFiles.map(item => <div key={item.id} className="flex min-w-0 items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5" data-testid={`pw-draft-file-${item.id}`}>
                            <FileText className="h-5 w-5 shrink-0 text-slate-400" />
                            <div className="min-w-0 flex-1"><p className="truncate text-xs text-slate-600" title={item.file.name} data-testid={`pw-draft-name-${item.id}`}>{item.file.name}</p><p className="text-[10px] text-slate-400" data-testid={`pw-draft-size-${item.id}`}>{fmtSize(item.file.size)}</p></div>
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={sending} title="Retirer le fichier" aria-label={`Retirer ${item.file.name}`} data-testid={`pw-draft-remove-${item.id}`} onClick={() => {
                                const next = selectedFiles.filter(file => file.id !== item.id);
                                if (pendingFiles.current) updatePublication(publicationScope, { ...pendingFiles.current, files: next }); else setSelectedFiles(next);
                                setFileError(null);
                            }}><X className="h-4 w-4" /></Button>
                        </div>)}
                        {fileError && <p role="alert" className="text-xs text-red-600" data-testid="pw-file-error">{fileError}</p>}
                        <div className="flex flex-wrap items-center gap-1 border-t border-slate-100 pt-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFilePick}
                                className="hidden"
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.rtf,.odt,.ods,.odp,.zip,.json"
                                data-testid="pw-file-input"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={sending || voiceActive || selectedFiles.length >= 5}
                                className="h-8 w-8 text-slate-500"
                                data-testid="pw-btn-attach"
                                title="Joindre image, PDF, vidéo, audio…"
                                aria-label="Joindre un fichier"
                            >
                                <Paperclip className="w-4 h-4" />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => { setVoiceError(null); voice.start(); }}
                                disabled={sending || voiceActive || !!selectedFiles.length || !!pendingFiles.current}
                                className="h-8 w-8 text-slate-500"
                                data-testid="pw-btn-record"
                                title="Enregistrer un mémo vocal (max 3 min)"
                                aria-label="Enregistrer un vocal"
                            >
                                <Mic className="w-4 h-4" />
                            </Button>
                            <div className="flex-1" />
                            <Button
                                onClick={() => send()}
                                disabled={sending || voiceActive || (!text.trim() && !selectedFiles.length && !pendingFiles.current?.messageId)}
                                size="sm"
                                className="gap-1"
                                data-testid="pw-btn-send"
                            >
                                <Send className="w-4 h-4" /> {sending ? 'Envoi…' : fileError ? 'Réessayer' : 'Publier'}
                            </Button>
                        </div>
                        {!!selectedFiles.length && <p className="text-[10px] text-slate-400" data-testid="pw-files-limits">{selectedFiles.length}/5 fichiers · 500 Mo maximum par fichier</p>}
                    </div>
                ) : (
                    <div className="border-t pt-3 text-xs text-slate-400 italic text-center">
                        Vous n&apos;êtes pas membre de ce projet — lecture seule.
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default ProjectWall;
